import * as yup from 'yup';

import { CountriesList } from '@/shared/constants/countries-list';
import { FormLabelItem, TDtoWrapper } from '@/shared/lib/forms/form-wrapper.types';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';

import { ContactDto, ContactType } from '../../sj-orm';
import { JewelryDetails, JewelryGeneral } from '../../sj-orm/models/assets/categories/jewelry';
import {
  ItemConditionType,
  JewelryType,
  JewelryTypeName,
  MetalType,
  PreciousGemstonesTypeJewelry,
  TypeOfHistoryOfOriginType,
} from '../../sj-orm/models/assets/enums';
import { CollectionName } from '../../sj-orm/types';

import { assetsDocumentsBuilder, AssetsDocumentsPartType } from './builders/documents';
import { assetsOwnershipBuilder, AssetsOwnershipPartType } from './builders/ownership';
import { assetsProfitabilityBuilder, AssetsProfitabilityPartType } from './builders/profitability';
import { assetsStatisticsBuilder, AssetsStatisticsPartType } from './builders/statistics';
import { baseAssetFields } from './fields';

export type Jewelry = JewelryFormGeneralType &
  JewelryFormDetailsType &
  Omit<AssetsOwnershipPartType, 'createdAt' | 'updatedAt'> &
  Omit<AssetsProfitabilityPartType, 'createdAt' | 'updatedAt'> &
  AssetsStatisticsPartType &
  Omit<AssetsDocumentsPartType, 'createdAt' | 'updatedAt'>;

export const JewelryList = [
  {
    label: 'forms_assets:jewelery.enums.type.ring',
    key: JewelryType.ring,
  },
  {
    label: 'forms_assets:jewelery.enums.type.earrings',
    key: JewelryType.earrings,
  },
  {
    label: 'forms_assets:jewelery.enums.type.necklace',
    key: JewelryType.necklace,
  },
  {
    label: 'forms_assets:jewelery.enums.type.pendant',
    key: JewelryType.pendant,
  },
  {
    label: 'forms_assets:jewelery.enums.type.bracelet',
    key: JewelryType.bracelet,
  },
  {
    label: 'forms_assets:jewelery.enums.type.brooch',
    key: JewelryType.brooch,
  },
  {
    label: 'forms_assets:jewelery.enums.type.cufflinks',
    key: JewelryType.cufflinks,
  },
  {
    label: 'forms_assets:jewelery.enums.type.clasp',
    key: JewelryType.clasp,
  },
  {
    label: 'forms_assets:jewelery.enums.type.tiara',
    key: JewelryType.tiara,
  },
  {
    label: 'forms_assets:jewelery.enums.type.other',
    key: JewelryType.other,
  },
];

const JewelryTypeList = [
  {
    label: 'forms_assets:jewelery.enums.jeweleryType.brand',
    key: JewelryTypeName.brand,
  },
  {
    label: 'forms_assets:jewelery.enums.jeweleryType.relic',
    key: JewelryTypeName.relic,
  },
  {
    label: 'forms_assets:jewelery.enums.jeweleryType.customOrder',
    key: JewelryTypeName.customOrder,
  },
  {
    label: 'forms_assets:jewelery.enums.jeweleryType.vintage',
    key: JewelryTypeName.vintage,
  },
];

const MetalTypeList = [
  {
    label: 'forms_assets:jewelery.enums.metalType.whiteGold',
    key: MetalType.whiteGold,
  },
  {
    label: 'forms_assets:jewelery.enums.metalType.yellowGold',
    key: MetalType.yellowGold,
  },
  {
    label: 'forms_assets:jewelery.enums.metalType.roseGold',
    key: MetalType.roseGold,
  },
  {
    label: 'forms_assets:jewelery.enums.metalType.silver',
    key: MetalType.silver,
  },
  {
    label: 'forms_assets:jewelery.enums.metalType.platinum',
    key: MetalType.platinum,
  },
  {
    label: 'forms_assets:jewelery.enums.metalType.steel',
    key: MetalType.steel,
  },
  {
    label: 'forms_assets:jewelery.enums.metalType.titanium',
    key: MetalType.titanium,
  },
  {
    label: 'forms_assets:jewelery.enums.metalType.ceramic',
    key: MetalType.ceramic,
  },
  {
    label: 'forms_assets:jewelery.enums.metalType.other',
    key: MetalType.other,
  },
];

const PreciousGemstonesList = [
  {
    label: 'forms_assets:jewelery.enums.preciousGemstones.no',
    key: PreciousGemstonesTypeJewelry.no,
  },
  {
    label: 'forms_assets:jewelery.enums.preciousGemstones.diamond',
    key: PreciousGemstonesTypeJewelry.diamond,
  },
  {
    label: 'forms_assets:jewelery.enums.preciousGemstones.emerald',
    key: PreciousGemstonesTypeJewelry.emerald,
  },
  {
    label: 'forms_assets:jewelery.enums.preciousGemstones.ruby',
    key: PreciousGemstonesTypeJewelry.ruby,
  },
  {
    label: 'forms_assets:jewelery.enums.preciousGemstones.sapphire',
    key: PreciousGemstonesTypeJewelry.sapphire,
  },
  {
    label: 'forms_assets:jewelery.enums.preciousGemstones.alexandrite',
    key: PreciousGemstonesTypeJewelry.alexandrite,
  },
  {
    label: 'forms_assets:jewelery.enums.preciousGemstones.naturalPearl',
    key: PreciousGemstonesTypeJewelry.naturalPearl,
  },
  {
    label: 'forms_assets:jewelery.enums.preciousGemstones.spinel',
    key: PreciousGemstonesTypeJewelry.spinel,
  },
  {
    label: 'forms_assets:jewelery.enums.preciousGemstones.other',
    key: PreciousGemstonesTypeJewelry.other,
  },
];

const ItemConditionList = [
  {
    label: 'forms_assets:jewelery.enums.itemCondition.brandNew',
    key: ItemConditionType.brandNew,
  },
  {
    label: 'forms_assets:jewelery.enums.itemCondition.used',
    key: ItemConditionType.used,
  },
];

const TypeOfHistoryOfOriginList = [
  {
    label: 'forms_assets:gemstones.enums.typeOfHistoryOfOrigin.gift',
    key: TypeOfHistoryOfOriginType.gift,
  },
  {
    label: 'forms_assets:gemstones.enums.typeOfHistoryOfOrigin.purchase',
    key: TypeOfHistoryOfOriginType.purchase,
  },
  {
    label: 'forms_assets:gemstones.enums.typeOfHistoryOfOrigin.inheritance',
    key: TypeOfHistoryOfOriginType.inheritance,
  },
  {
    label: 'forms_assets:gemstones.enums.typeOfHistoryOfOrigin.other',
    key: TypeOfHistoryOfOriginType.other,
  },
];

type JewelryHelpGeneral = {
  nickName: QuestionType.TEXT;
  country: QuestionType.SELECT_FROM_LIST;
  type: QuestionType.SELECT_FROM_LIST;
  otherType: QuestionType.TEXT;
  purchaseValue: QuestionType.CURRENCY_CALCULATOR;
  currency: QuestionType.SELECT_FROM_LIST;
  kind: QuestionType.SELECT_FROM_LIST;
  brand: QuestionType.TEXT;
  storageLocation: QuestionType.TEXT;
};

type JewelryHelpDetails = {
  collection?: QuestionType.CHECKBOX;
  collectionName?: QuestionType.TEXT;
  metal?: QuestionType.SELECT_FROM_LIST;
  otherMetal?: QuestionType.TEXT;
  preciousGemstones?: QuestionType.SELECT_FROM_LIST;
  otherPreciousGemstones?: QuestionType.TEXT;
  preciousGemstonesWeight?: QuestionType.NUMBER;
  preciousGemstonesColor?: QuestionType.TEXT;
  serialNumber?: QuestionType.TEXT;
  acquisitionDate?: QuestionType.DATE_TIME;
  receipt?: QuestionType.PICK_ARRAY_OF_DTO;
  paymentMethod?: QuestionType.TEXT;
  purchaseLocation?: QuestionType.TEXT;
  registrationInBrandDatabase?: QuestionType.CHECKBOX;
  phoneNumber?: QuestionType.PHONE_NUMBER;
  email?: QuestionType.EMAIL;
  personalInformation?: QuestionType.PICK_ARRAY_OF_DTO;
  managerSellerContact?: QuestionType.TEXT;
  packagingAvailability?: QuestionType.CHECKBOX;
  itemCondition?: QuestionType.SELECT_FROM_LIST;
  defects?: QuestionType.TEXT;
  typeOfHistoryOfOrigin?: QuestionType.SELECT_FROM_LIST;
  otherTypeOfHistoryOfOrigin?: QuestionType.TEXT;
  occasion?: QuestionType.TEXT;
  source?: QuestionType.TEXT;
  attachments?: QuestionType.FILE_UPLOAD;
};

type JewelryFormGeneralType = TDtoWrapper<JewelryGeneral, JewelryHelpGeneral>;
type JewelryFormDetailsType = TDtoWrapper<JewelryDetails, JewelryHelpDetails>;

const JewelryFormGeneral: JewelryFormGeneralType = {
  nickName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:common.inputs.nickname.label',
    value: '',
    required: true,
    yupScheme: yup.string().required('forms_assets:common.inputs.nickname.errors.required'),
    colSpan: 2,
  },
  country: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:jewelery.inputs.country.label',
    value: '',
    required: true,
    items: CountriesList,
    withSearch: true,
  },
  type: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:jewelery.inputs.type.label',
    value: JewelryType.ring,
    items: JewelryList,
    required: true,
  },
  otherType: {
    type: QuestionType.TEXT,
    label: 'forms_assets:jewelery.inputs.otherType.label',
    showIf: {
      type: {
        value: JewelryType.other,
      },
    },
    value: '',
    required: true,
  },
  purchaseValue: {
    type: QuestionType.CURRENCY_CALCULATOR,
    label: 'forms_assets:jewelery.inputs.personalAppraisal.label',
    tooltip: 'forms_assets:jewelery.inputs.personalAppraisal.tooltip',
    equivalentValueTooltip: 'forms_assets:common.inputs.equivalentValue.tooltip',
    value: 0,
    required: true,
  },
  kind: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:jewelery.inputs.kind.label',
    value: JewelryTypeName.brand,
    items: JewelryTypeList,
  },
  brand: {
    type: QuestionType.TEXT,
    label: 'forms_assets:jewelery.inputs.brand.label',
    value: '',
  },
  storageLocation: {
    type: QuestionType.TEXT,
    label: 'forms_assets:jewelery.inputs.storageLocation.label',
    value: '',
    required: true,
  },
};

const JewelryFormDetails: JewelryFormDetailsType = {
  collection: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:jewelery.inputs.collection.label',
    order: 3,
  },
  collectionName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:jewelery.inputs.collectionName.label',
    showIf: {
      collection: {
        value: true,
      },
    },
    order: 4,
  },
  metal: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:jewelery.inputs.metal.label',
    items: MetalTypeList,
    mode: 'multiple',
    order: 5,
  },
  otherMetal: {
    type: QuestionType.TEXT,
    label: 'forms_assets:jewelery.inputs.otherMetal.label',
    showIf: {
      metal: {
        value: MetalType.other,
      },
    },
    order: 6,
  },
  preciousGemstones: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:jewelery.inputs.preciousGemstones.label',
    items: PreciousGemstonesList,
    mode: 'multiple',
    order: 7,
  },
  otherPreciousGemstones: {
    type: QuestionType.TEXT,
    label: 'forms_assets:jewelery.inputs.otherPreciousGemstones.label',
    showIf: {
      preciousGemstones: {
        value: PreciousGemstonesTypeJewelry.other,
      },
    },
    order: 8,
  },
  preciousGemstonesWeight: {
    type: QuestionType.NUMBER,
    label: 'forms_assets:jewelery.inputs.preciousGemstonesWeight.label',
    order: 9,
  },
  preciousGemstonesColor: {
    type: QuestionType.TEXT,
    label: 'forms_assets:jewelery.inputs.preciousGemstonesColor.label',
    order: 10,
  },
  serialNumber: {
    type: QuestionType.TEXT,
    label: 'forms_assets:jewelery.inputs.serialNumber.label',
    order: 11,
  },
  acquisitionDate: {
    type: QuestionType.DATE_TIME,
    label: 'forms_assets:jewelery.inputs.acquisitionDate.label',
    order: 12,
  },
  receipt: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    items: [CollectionName.PRIVATE_DOCUMENTS],
    label: 'forms_assets:jewelery.inputs.purchaseReceipt.label',
    order: 13,
  },
  paymentMethod: {
    type: QuestionType.TEXT,
    label: 'forms_assets:jewelery.inputs.paymentMethod.label',
    order: 14,
  },
  purchaseLocation: {
    type: QuestionType.TEXT,
    label: 'forms_assets:jewelery.inputs.purchaseLocation.label',
    order: 15,
  },
  brandDatabaseLabel: {
    type: FormLabelItem.label,
    label: 'forms_assets:jewelery.inputs.brandDatabaseLabel.label',
    order: 16,
  },
  registrationInBrandDatabase: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:jewelery.inputs.registrationInBrandDatabase.label',
    order: 21,
    colSpan: 2,
  },
  phoneNumber: {
    type: QuestionType.PHONE_NUMBER,
    label: 'forms_assets:common.inputs.phoneNumber.label',
    order: 17,
  },
  email: {
    type: QuestionType.EMAIL,
    label: 'forms_assets:common.inputs.email.label',
    order: 18,
  },
  personalInformation: {
    type: QuestionType.PICK_ONE_OF_DTOS,
    label: 'forms_assets:jewelery.inputs.personalInformation.label',
    colName: CollectionName.CONTACTS,
    dtoType: [ContactType.BANK],
    filter: (contact) => ContactType.BANK === (contact as unknown as ContactDto).type,
    order: 19,
  },
  managerSellerContact: {
    type: QuestionType.TEXT,
    label: 'forms_assets:jewelery.inputs.managerSellerContact.label',
    order: 20,
  },
  packagingAvailability: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:jewelery.inputs.packagingAvailability.label',
    order: 22,
  },
  itemCondition: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:jewelery.inputs.itemCondition.label',
    items: ItemConditionList,
    order: 23,
  },
  defects: {
    type: QuestionType.TEXT,
    label: 'forms_assets:jewelery.inputs.defects.label',
    order: 24,
  },
  historyOfOrigin: {
    type: FormLabelItem.label,
    label: 'forms_assets:gemstones.inputs.historyOfOrigin.label',
    order: 25,
  },
  typeOfHistoryOfOrigin: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:gemstones.inputs.typeOfHistoryOfOrigin.label',
    items: TypeOfHistoryOfOriginList,
    order: 26,
  },
  otherTypeOfHistoryOfOrigin: {
    type: QuestionType.TEXT,
    label: 'forms_assets:gemstones.inputs.otherTypeOfHistoryOfOrigin.label',
    showIf: {
      typeOfHistoryOfOrigin: {
        value: TypeOfHistoryOfOriginType.other,
      },
    },
    order: 27,
  },
  occasion: {
    type: QuestionType.TEXT,
    label: 'forms_assets:gemstones.inputs.occasion.label',
    tooltip: 'forms_assets:gemstones.inputs.occasion.tooltip',
    order: 28,
  },
  source: {
    type: QuestionType.TEXT,
    label: 'forms_assets:gemstones.inputs.source.label',
    tooltip: 'forms_assets:gemstones.inputs.source.tooltip',
    order: 29,
  },
  attachments: baseAssetFields.attachments,
};

const JewelryForm = [
  JewelryFormGeneral,
  JewelryFormDetails,
  assetsOwnershipBuilder([
    'contactInfoLabel',
    'contactInfoLabelDescription',
    'ownerAddress',
    'phoneNumber',
    'email',
  ]).form,
  assetsProfitabilityBuilder(['independentAppraisalAmount']),
  assetsStatisticsBuilder([
    'taxAgentWithBroker',
    'taxAgent',
    'publicFundTaxAgent',
    'taxAgentOther',
    'taxAgentWithBrokerOther',
    'publicTaxAgentOther',
    'legalTaxAgent',
    'legalTaxAgentOther',
    'holderTaxAgent',
    'holderTaxAgentOther',
    'specificsOfTaxation',
    'profitabilityFact',
    'profitabilityPlan',
  ]).form,
  assetsDocumentsBuilder(),
];

export { JewelryForm };
